import { useState, type HTMLAttributes } from 'react';
import { cx } from '@parrot/utils';
import styles from './Avatar.module.css';

type Props = Omit<HTMLAttributes<HTMLSpanElement>, 'children'> & {
  /** Shown in preference to the initials. Falls back to them if it fails to load. */
  src?: string;
  /** Supplies both the initials and the accessible name. */
  name: string;
  size?: 'sm' | 'md' | 'lg';
  /** Selected — the menu it belongs to is open, say. */
  active?: boolean;
};

/** Array.from iterates code points, so a surrogate pair survives as one character. */
const initial = (word: string | undefined) => Array.from(word ?? '')[0] ?? '';

/** First and last, so a middle name doesn't displace the surname. */
const toInitials = (name: string) => {
  const words = name.trim().split(/\s+/);
  const last = words.length > 1 ? words.at(-1) : undefined;
  return (initial(words[0]) + initial(last)).toUpperCase();
};

/**
 * A person, as an image or their initials.
 *
 * Not interactive and has no hover state — an avatar inside a menu button is the
 * button's business. `name` is required because it is both the accessible name and
 * the fallback when there is no image or it fails to load; pass `aria-hidden` when
 * the name already sits in the text beside it.
 */
export const Avatar = ({
  src,
  name,
  size = 'md',
  active = false,
  className,
  ...spanProps
}: Props) => {
  // Keyed by src rather than a boolean so a new src retries without an effect.
  const [failedSrc, setFailedSrc] = useState<string>();
  const showImage = src !== undefined && src !== failedSrc;

  return (
    <span
      className={cx(styles.base, styles[size], active && styles.active, className)}
      role={'img'}
      aria-label={name}
      {...spanProps}
    >
      {showImage ? (
        <img src={src} alt={''} className={styles.image} onError={() => setFailedSrc(src)} />
      ) : (
        toInitials(name)
      )}
    </span>
  );
};
